/**
 * Auth context — owns the current PrivateUser, exposes login/register/logout
 * helpers, and bootstraps from /api/auth/me on first mount.
 *
 * The hook is in `./useAuth.ts` so this file can stay component-only and
 * play nicely with React Refresh.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { api, ApiResponseError } from './api';
import type { PrivateUser } from './auth-types';
import { AuthContext, type AuthContextValue } from './auth-context';

interface MeResponse {
  user: PrivateUser | null;
}

interface AuthSuccessResponse {
  user: PrivateUser;
  // The CSRF token is returned in the body for non-cookie clients (CLI, tests,
  // mobile). The browser client reads it from the ct_csrf cookie via api.ts,
  // so the body value here is unused — keep the field so the response shape
  // matches the wire contract.
  csrf: string;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<PrivateUser | null>(null);
  const [status, setStatus] = useState<'loading' | 'authenticated' | 'anonymous'>(
    'loading',
  );
  const mounted = useRef(true);

  const refresh = useCallback(async () => {
    try {
      const res = await api.get<MeResponse>('/auth/me');
      if (!mounted.current) return;
      setUser(res.user);
      setStatus(res.user ? 'authenticated' : 'anonymous');
    } catch {
      if (!mounted.current) return;
      setUser(null);
      setStatus('anonymous');
    }
  }, []);

  // Bootstrap once on mount — setState runs only inside async callbacks, so
  // the react-hooks/set-state-in-effect rule does not fire here.
  useEffect(() => {
    mounted.current = true;
    let cancelled = false;
    void api.get<MeResponse>('/auth/me').then(
      (res) => {
        if (cancelled || !mounted.current) return;
        setUser(res.user);
        setStatus(res.user ? 'authenticated' : 'anonymous');
      },
      () => {
        if (cancelled || !mounted.current) return;
        setUser(null);
        setStatus('anonymous');
      },
    );
    return () => {
      cancelled = true;
      mounted.current = false;
    };
  }, []);

  const login = useCallback<AuthContextValue['login']>(async (input) => {
    const res = await api.post<AuthSuccessResponse>('/auth/login', input);
    setUser(res.user);
    setStatus('authenticated');
    return res.user;
  }, []);

  const register = useCallback<AuthContextValue['register']>(async (input) => {
    const res = await api.post<AuthSuccessResponse>('/auth/register', input);
    setUser(res.user);
    setStatus('authenticated');
    return res.user;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api.post('/auth/logout');
    } catch (err) {
      // 403 means our CSRF cookie expired — we still want to forget the user
      // client-side.
      if (!(err instanceof ApiResponseError) || err.status !== 403) {
        throw err;
      }
    } finally {
      setUser(null);
      setStatus('anonymous');
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ user, status, refresh, login, register, logout, setUser }),
    [user, status, refresh, login, register, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
